import useCatalog from 'stores/catalog';
import { UseIncentiveCampaignReturn } from './interfaces';

/**
 * Hook responsible for retrieving and computing
 * the current incentive campaign information for the selected POS.
 *
 * This hook:
 * - Retrieves the currently selected POS from the store
 * - Fetches the active campaign associated with that POS
 * - Computes the campaign progress percentage
 *
 * Progress is calculated using a simple proportional formula:
 *
 * `(packagesReachedQuantity / packagesQuantity) * 100`
 *
 * If campaign data is unavailable, progress defaults to `0`.
 *
 * @returns {UseIncentiveCampaignReturn}
 *
 * @example
 * ```ts
 * const { data, isFetching, progress } = useIncentiveCampaign();
 *
 * if (!isFetching && data) {
 *   console.log(`Campaign progress: ${progress}%`);
 * }
 * ```
 */
export function useIncentiveCampaign(): UseIncentiveCampaignReturn {
	const {
		catalogStore: { incentiveCurrentCampaignData: data, incentiveCampaignLoading: isFetching },
	} = useCatalog();

	const rawProgress = ((data?.packagesReachedQuantity || 0) / (data?.packagesQuantity || 1)) * 100;

	const progress = rawProgress >= 100 ? 100 : Math.min(99, Math.round(rawProgress));

	return {
		data,
		isFetching: !!isFetching,
		progress,
	};
}
